import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import Slide from '@material-ui/core/Slide'
import Button from '@material-ui/core/Button'
import DialogActions from '@material-ui/core/DialogActions'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import FormControl from '@material-ui/core/FormControl'
import FormLabel from '@material-ui/core/FormLabel'
import FormGroup from '@material-ui/core/FormGroup'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import Clear from '@material-ui/icons/Clear'
import Select from '@mui/material/Select'
import MenuItem from '@material-ui/core/MenuItem'
import EnergyAPI from 'config/api/EnergyAPI'
import { onError } from 'config/lib/errorLib'
import { eeProductOptions } from 'config/constants/Constants'
import TestPayloadDialog from './TestPayloadDialog'

import componentStylesDialog from 'assets/theme/components/dialog.js'
import componentStylesForms from 'assets/theme/components/forms.js'
import componentStylesButtons from 'assets/theme/components/button.js'

const useStylesDialog = makeStyles(componentStylesDialog)
const useStylesForms = makeStyles(componentStylesForms)
const useStylesButtons = makeStyles(componentStylesButtons)

const ALL_IRISH_COUNTIES = [
    'Carlow','Cavan','Clare','Cork','Donegal','Dublin',
    'Galway','Kerry','Kildare','Kilkenny','Laois','Leitrim',
    'Limerick','Longford','Louth','Mayo','Meath','Monaghan',
    'Offaly','Roscommon','Sligo','Tipperary','Waterford',
    'Westmeath','Wexford','Wicklow'
]

const SOURCE_LABELS = { ee: 'Energy Efficiency (EE)', sf: 'Solar Finder (SF)' }

const Transition = React.forwardRef(function Transition(props, ref) {
  	return <Slide direction='down' ref={ref} {...props} />
})

export default function EnergyDeliveryModal(props) {
	const classes = { ...useStylesDialog(), ...useStylesForms(), ...useStylesButtons() }
  	const api = new EnergyAPI()

    const delivery = props.delivery
    const companies = props.companies

    const [product, setProduct] = useState(delivery.product)
    const [leadPrice, setLeadPrice] = useState(delivery.leadPrice)
    const [deliveryTypes, setDeliveryTypes] = useState(() => {
        const types = []
        if (delivery.webhook) types.push('webhook')
        if (delivery.deliveryEmail) types.push('email')
        if (delivery.solarStackWebhook) types.push('solarStack')
        if (delivery.privyrWebhook) types.push('privyr')
        return types.length ? types : ['webhook']
    })
    const [webhook, setWebhook] = useState(delivery.webhook || '')
    const [deliveryEmail, setDeliveryEmail] = useState(delivery.deliveryEmail || '')
    const [solarStackWebhook, setSolarStackWebhook] = useState(delivery.solarStackWebhook || '')
    const [privyrWebhook, setPrivyrWebhook] = useState(delivery.privyrWebhook || '')
    const [testDialog, setTestDialog] = useState({ open: false, kind: null, product: null, payload: null })
    const [weeklyCap, setWeeklyCap] = useState(delivery.weeklyCap != null ? delivery.weeklyCap : '')
    // Confirmation dialog for choosing when a cap change takes effect (now vs next week).
    const [capChangeDialog, setCapChangeDialog] = useState(false)
    const [minEELeads, setMinEELeads] = useState(delivery.minEELeads != null ? delivery.minEELeads : '')
    const [eeLeadMode, setEeLeadMode] = useState(delivery.eeLeadMode || '')
    const [active, setActive] = useState(delivery.active === true ? true : false)
    const [priority, setPriority] = useState(delivery.priority === true ? true : false)
    const [surgeEnabled, setSurgeEnabled] = useState(delivery.surgeEnabled === true ? true : false)
    const [surgePercent, setSurgePercent] = useState(delivery.surgePercent != null ? delivery.surgePercent : '')
    const [weekendLeads, setWeekendLeads] = useState(delivery.weekendLeads !== false)
    const [businessHoursOnly, setBusinessHoursOnly] = useState(delivery.businessHoursOnly === true)
    const [rejectDuplicates, setRejectDuplicates] = useState(delivery.rejectDuplicates === true)
    const [hkCommission, setHkCommission] = useState(delivery.hkCommission === true)
    const [preHkLeads, setPreHkLeads] = useState(delivery.preHkLeads != null ? delivery.preHkLeads : 0)
    const [billingMode, setBillingMode] = useState(delivery.billingMode || 'actual')
    const [billingCompanyId, setBillingCompanyId] = useState(delivery.billingCompanyId || '')
    // Per-source overrides: { ee?: {...}, sf?: {...} } → { enabled, leadPrice, weeklyCap, counties }
    const [sourceOverrides, setSourceOverrides] = useState(() => {
        const init = {}
        const existing = delivery.sourceOverrides || {}
        for (const src of ['ee', 'sf']) {
            const ov = existing[src]
            init[src] = {
                enabled: !!ov,
                leadPrice: ov && ov.leadPrice != null ? ov.leadPrice : '',
                weeklyCap: ov && ov.weeklyCap != null ? ov.weeklyCap : '',
                counties: ov && Array.isArray(ov.counties) ? ov.counties : []
            }
        }
        return init
    })

    function setOverrideField(src, field, value) {
        setSourceOverrides(prev => ({
            ...prev,
            [src]: { ...prev[src], [field]: value }
        }))
    }

    // Build the API payload map from enabled override rows (null when none).
    function buildSourceOverridesPayload() {
        const out = {}
        for (const src of ['ee', 'sf']) {
            const ov = sourceOverrides[src]
            if (!ov || !ov.enabled) continue
            out[src] = {
                leadPrice: ov.leadPrice === '' ? null : parseFloat(ov.leadPrice),
                weeklyCap: ov.weeklyCap === '' ? null : parseInt(ov.weeklyCap),
                counties: Array.isArray(ov.counties) ? ov.counties : []
            }
        }
        return Object.keys(out).length ? out : null
    }

    function buildQuoteForProduct(testProduct) {        switch (testProduct) {
            case 'EV Charger':
                return {
                    hasElectricCar: 'Have',
                    hasGrantInterest: 'YES',
                    hasParking: 'YES',
                    installLocation: 'Detached house',
                    timeline: 'In the next 6 months'
                }
            case 'Insulation':
                return {
                    insulationType: 'Attic insulation',
                    hasGrantInterest: 'YES',
                    timeline: 'In the next 6 months'
                }
            case 'Boiler':
                return {
                    boilerType: 'Gas',
                    hasGrantInterest: 'YES',
                    timeline: 'In the next 6 months'
                }
            case 'Heat Pump':
                return {
                    homeBuiltPeriod: 'Before 2021',
                    currentHeating: 'Oil boiler',
                    propertyType: 'Semi-detached',
                    homeSize: '150–200 m²',
                    hasBerCert: 'Yes',
                    berRating: 'C2',
                    hli: '2.1',
                    underfloorHeating: 'Yes, in some rooms',
                    radiatorCount: '11–15',
                    loanInterest: 'Yes'
                }
            case 'Solar':
            case 'Commercial Solar':
            default:
                return {
                    mostImportant: 'Reducing my electricity bills',
                    howMuch: '10 panels',
                    timeline: 'Within 3 months',
                    electricBills: '€200/month',
                    stories: '2',
                    hasGrantInterest: 'Yes'
                }
        }
    }

    function buildDefaultLead(testProduct) {
        const resolvedProduct = testProduct || delivery.product
        return {
            product: resolvedProduct,
            leadId: 'test-' + Date.now(),
            leadNumber: 'TEST0001',
            firstName: 'Test',
            lastName: 'User',
            email: 'testing@bq.ie',
            phone: '0851234567',
            county: 'Dublin',
            eircode: 'D01AB12',
            date: new Date().toISOString().slice(0, 10),
            createdAt: Date.now(),
            source: 'TEST',
            stage: 'new',
            isValid: true,
            quote: buildQuoteForProduct(resolvedProduct)
        }
    }

    function buildSolarStackNotes(testProduct, quote) {
        const q = quote || {}
        const grant = q.hasGrantInterest === true || q.hasGrantInterest === 'Yes' ? 'Yes'
            : (q.hasGrantInterest === false || q.hasGrantInterest === 'No' ? 'No' : (q.hasGrantInterest || ''))
        if (!testProduct || testProduct === 'Solar' || testProduct === 'Commercial Solar') {
            return `What appeals to you most about solar? ${q.mostImportant || ''} | How much are your electricity bills? ${q.electricBills || ''} | How much solar would you like? ${q.howMuch || ''} | What's your timeframe for getting solar? ${q.timeline || ''} | How many storeys does your house have? ${q.stories || ''} | Are you interested in availing of the solar grant? ${grant}`
        }
        const labels = {
            mostImportant: 'Most important',
            electricBills: 'Electricity bills',
            howMuch: 'How much',
            timeline: 'Timeframe',
            stories: 'Storeys',
            hasGrantInterest: 'Grant interest',
            hasElectricCar: 'Electric car',
            hasParking: 'Off-street parking',
            installLocation: 'Install location',
            insulationType: 'Insulation type',
            insulationArea: 'Insulation area',
            boilerType: 'Boiler type'
        }
        return Object.keys(q)
            .map((key) => `${labels[key] || key}: ${q[key] == null ? '' : q[key]}`)
            .join(' | ')
    }

    function buildPrivyrPayload(lead) {
        const l = lead || {}
        const q = l.quote || {}
        const grant = q.hasGrantInterest === true || q.hasGrantInterest === 'Yes' ? 'Yes'
            : (q.hasGrantInterest === false || q.hasGrantInterest === 'No' ? 'No' : (q.hasGrantInterest || ''))
        const fullName = `${l.firstName || ''} ${l.lastName || ''}`.trim()
        return {
            name: fullName,
            email: l.email || '',
            phone: l.phone || '',
            display_name: l.firstName || fullName,
            other_fields: {
                County: l.county || '',
                Eircode: l.eircode || '',
                'Most Important': q.mostImportant || '',
                'How Much Solar': q.howMuch || '',
                Timeline: q.timeline || '',
                'Electricity Bills': q.electricBills || '',
                Storeys: q.stories || '',
                'Grant Interest': grant,
                Source: 'EnergyEfficiency'
            }
        }
    }

    function buildTestPayload(kind, testProduct) {
        const lead = buildDefaultLead(testProduct)
        if (kind === 'webhook') {
            return { ...lead, site: 'ee' }
        }
        if (kind === 'solarStack') {
            return {
                firstName: lead.firstName,
                lastName: lead.lastName,
                date: lead.date,
                email: lead.email,
                phone: lead.phone,
                county: lead.county,
                eircode: lead.eircode,
                notes: buildSolarStackNotes(lead.product, lead.quote),
                source: 'BeQuick'
            }
        }
        if (kind === 'privyr') {
            return buildPrivyrPayload(lead)
        }
        return lead
    }

    function openWebhookTest() {
        setTestDialog({
            kind: 'webhook',
            open: true,
            product: delivery.product,
            payload: buildTestPayload('webhook', delivery.product)
        })
    }

    function openEmailTest() {
        setTestDialog({
            kind: 'email',
            open: true,
            product: delivery.product,
            payload: buildTestPayload('email', delivery.product)
        })
    }

    function openSolarStackTest() {
        setTestDialog({
            kind: 'solarStack',
            open: true,
            product: delivery.product,
            payload: buildTestPayload('solarStack', delivery.product)
        })
    }

    function openPrivyrTest() {
        setTestDialog({
            kind: 'privyr',
            open: true,
            product: delivery.product,
            payload: buildTestPayload('privyr', delivery.product)
        })
    }

    function changeTestProduct(nextProduct) {
        setTestDialog((prev) => ({
            ...prev,
            product: nextProduct,
            payload: buildTestPayload(prev.kind, nextProduct)
        }))
    }

    function closeTestDialog() {
        setTestDialog({ open: false, kind: null, product: null, payload: null })
    }

    async function sendTest(payload) {
        try {
            let result
            const testProduct = testDialog.product || delivery.product
            if (testDialog.kind === 'webhook') {
                result = await api.testWebhookDelivery(webhook, testProduct, payload && payload.site ? payload.site : 'ee', payload)
            } else if (testDialog.kind === 'email') {
                result = await api.testEmailDelivery(delivery.companyId, testProduct, deliveryEmail || null, payload)
            } else if (testDialog.kind === 'solarStack') {
                result = await api.testSolarStackDelivery(solarStackWebhook, payload)
            } else if (testDialog.kind === 'privyr') {
                result = await api.testPrivyrDelivery(privyrWebhook, payload)
            }
            if (result && result.success === false) {
                onError(new Error(result.message || 'Test delivery failed'))
            } else {
                props.showSuccessAlert('Test sent!')
                closeTestDialog()
            }
        } catch(e) {
            onError(e)
        }
    }

    async function updateDelivery(effectiveOverride) {
        try {
            const effective = effectiveOverride || 'now'
            let updatedDelivery = await api.updateEnergyDelivery(
                delivery.companyId, 
                delivery.product, 
                leadPrice, 
                deliveryTypes.includes('webhook') ? (webhook || null) : null,
                deliveryTypes.includes('email') ? (deliveryEmail || null) : null,
                deliveryTypes.includes('solarStack') ? (solarStackWebhook || null) : null,
                deliveryTypes.includes('privyr') ? (privyrWebhook || null) : null,
                weeklyCap === '' ? null : parseInt(weeklyCap), 
                active,
                minEELeads === '' ? null : parseInt(minEELeads),
                eeLeadMode || null,
                priority,
                surgeEnabled,
                surgePercent === '' ? null : parseInt(surgePercent),
                weekendLeads,
                businessHoursOnly,
                hkCommission,
                preHkLeads === '' ? 0 : parseInt(preHkLeads),
                billingMode,
                billingCompanyId || null,
                buildSourceOverridesPayload(),
                rejectDuplicates,
                effective
            )
            console.log('delivery: ', updatedDelivery)

            setCapChangeDialog(false)
            props.updateDelivery(updatedDelivery)
            props.showSuccessAlert('Delivery updated!')
            props.close()
        } catch(e) {
            setCapChangeDialog(false)
            onError(e)
        }
    }

    // Save: if the weekly cap changed, ask now-or-next in a dialog; otherwise save immediately.
    function handleSave() {
        const parsedCap = weeklyCap === '' ? null : parseInt(weeklyCap)
        const currentCap = delivery.weeklyCap != null ? delivery.weeklyCap : null
        if (parsedCap !== currentCap) {
            setCapChangeDialog(true)
            return
        }
        updateDelivery('now')
    }

	return (
        <Dialog
            open={props.isOpen}
            TransitionComponent={Transition}
            keepMounted
            onClose={() => props.close()}
        >
            <div className={classes.dialogHeader}>
				<Typography
					variant='h5'
					component='h5'
					className={classes.dialogTitle}
				>
					Update Energy Delivery
				</Typography>
				<IconButton onClick={() => props.close()}>
					<Clear />
				</IconButton>
			</div>
            <DialogContent>
                <FormGroup>
                    <FormLabel>Company</FormLabel>
                    <FormControl variant='outlined' fullWidth>
                        <Select
                            multiple={false}
                            value={delivery.companyId}
                            input={<OutlinedInput />}
                            style={{marginBottom: '0.5rem'}}
                            disabled
                        >
                            {companies.map((company) => (
                                <MenuItem key={company.companyId} value={company.companyId}>{company.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </FormGroup>
                <FormGroup>
                    <FormLabel>Product</FormLabel>
                    <FormControl variant='outlined' fullWidth>
                        <Select
                            multiple={false}
                            value={product}
                            onChange={(e) => setProduct(e.target.value)}
                            input={<OutlinedInput />}
                            style={{marginBottom: '0.5rem'}}
                            disabled
                        >
                            {eeProductOptions.map((product) => (
                                <MenuItem key={product.value} value={product.value}>{product.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </FormGroup>
                <FormGroup>
                  	<FormLabel>Lead Price (€)</FormLabel>
                  	<OutlinedInput
                    	fullWidth
                    	type='number'
                        value={leadPrice}
                    	onChange={(e) => setLeadPrice(e.target.value)}
                  	/>
                </FormGroup>
                <FormGroup>
                  	<FormLabel>Delivery Type</FormLabel>
                  	<FormControl variant='outlined' fullWidth>
                        <Select
                            multiple
                            value={deliveryTypes}
                            onChange={(e) => {
                                setDeliveryTypes(e.target.value)
                            }}
                            input={<OutlinedInput />}
                            renderValue={(selected) => selected.map(v => v === 'webhook' ? 'Webhook' : v === 'email' ? 'Email' : v === 'privyr' ? 'Privyr' : 'SolarStack').join(', ')}
                            style={{marginBottom: '0.5rem'}}
                        >
                            <MenuItem value='webhook'>Webhook</MenuItem>
                            <MenuItem value='email'>Email</MenuItem>
                            <MenuItem value='solarStack'>SolarStack</MenuItem>
                            <MenuItem value='privyr'>Privyr</MenuItem>
                        </Select>
                    </FormControl>
                </FormGroup>
                {deliveryTypes.includes('webhook') && (
                    <FormGroup>
                      	<FormLabel>Webhook URL</FormLabel>
                      	<OutlinedInput
                        	fullWidth
                        	type='text'
                            value={webhook}
                        	onChange={(e) => setWebhook(e.target.value)}
                      	/>
                        <Box marginTop='0.5rem'>
                            <Button
                                color='secondary'
                                variant='outlined'
                                size='small'
                                disabled={!webhook}
                                onClick={openWebhookTest}
                            >
                                Test Webhook
                            </Button>
                        </Box>
                    </FormGroup>
                )}
                {deliveryTypes.includes('email') && (
                    <FormGroup>
                      	<FormLabel>Delivery Email</FormLabel>
                      	<OutlinedInput
                        	fullWidth
                        	type='text'
                            value={deliveryEmail}
							placeholder='e.g. leads@company.ie, manager@company.ie'
                        	onChange={(e) => setDeliveryEmail(e.target.value)}
                      	/>
                        <Box marginTop='0.5rem'>
                            <Button
                                color='secondary'
                                variant='outlined'
                                size='small'
                                disabled={!deliveryEmail}
                                onClick={openEmailTest}
                            >
                                Test Email Delivery
                            </Button>
                        </Box>
                    </FormGroup>
                )}
                {deliveryTypes.includes('solarStack') && (
                    <FormGroup>
                      	<FormLabel>SolarStack Webhook URL</FormLabel>
                      	<OutlinedInput
                        	fullWidth
                        	type='text'
                            value={solarStackWebhook}
							placeholder='https://api.solarstack.io/...'
                        	onChange={(e) => setSolarStackWebhook(e.target.value)}
                      	/>
                        <Box marginTop='0.5rem'>
                            <Button
                                color='secondary'
                                variant='outlined'
                                size='small'
                                disabled={!solarStackWebhook}
                                onClick={openSolarStackTest}
                            >
                                Test SolarStack Webhook
                            </Button>
                        </Box>
                    </FormGroup>
                )}
                {deliveryTypes.includes('privyr') && (
                    <FormGroup>
                      	<FormLabel>Privyr Webhook URL</FormLabel>
                      	<OutlinedInput
                        	fullWidth
                        	type='text'
                            value={privyrWebhook}
							placeholder='https://www.privyr.com/api/v1/incoming-leads/...'
                        	onChange={(e) => setPrivyrWebhook(e.target.value)}
                      	/>
                        <Box marginTop='0.5rem'>
                            <Button
                                color='secondary'
                                variant='outlined'
                                size='small'
                                disabled={!privyrWebhook}
                                onClick={openPrivyrTest}
                            >
                                Test Privyr Webhook
                            </Button>
                        </Box>
                    </FormGroup>
                )}
                <FormGroup>
                    <FormLabel>Weekly Cap</FormLabel>
                    <OutlinedInput
                        fullWidth
                        type='number'
                        value={weeklyCap}
                        placeholder='Leave blank for unlimited'
                        onChange={(e) => setWeeklyCap(e.target.value)}
                    />
                    <Box fontSize='11px' color='#8898aa' marginTop='0.25rem'>
                        Leave blank for unlimited (no cap)
                    </Box>
                </FormGroup>
                {delivery.scheduledWeeklyCap != null && (
                    <FormGroup>
                        <FormLabel>Scheduled Cap Change</FormLabel>
                        <Box
                            padding='0.75rem 1rem'
                            borderRadius='4px'
                            border='1px solid #5e72e4'
                            backgroundColor='#f4f5ff'
                            fontWeight='600'
                            color='#5e72e4'
                        >
                            {delivery.weeklyCap != null ? delivery.weeklyCap : '∞'} → {delivery.scheduledWeeklyCap} leads
                            <Box fontSize='11px' color='#8898aa' fontWeight='400' marginTop='0.25rem'>
                                Applies automatically after Saturday's invoice cron.
                            </Box>
                            <Box marginTop='0.5rem'>
                                <Button size='small' color='primary' onClick={() => updateDelivery('cancel')}>
                                    Cancel scheduled change
                                </Button>
                            </Box>
                        </Box>
                    </FormGroup>
                )}
                {product === 'Solar' && (
                    <FormGroup>
                        <FormLabel>Source Overrides</FormLabel>
                        <Box fontSize='11px' color='#8898aa' marginBottom='0.5rem'>
                            Optionally give a specific lead source its own price, weekly cap and counties. Sources without an override use the base Lead Price / Weekly Cap / coverage above. Each override is billed and capped independently.
                        </Box>
                        {['ee', 'sf'].map((src) => {
                            const ov = sourceOverrides[src]
                            return (
                                <Box key={src} border='1px solid #e9ecef' borderRadius='4px' padding='0.75rem' marginBottom='0.5rem'>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>
                                        <input
                                            type='checkbox'
                                            checked={ov.enabled}
                                            onChange={(e) => setOverrideField(src, 'enabled', e.target.checked)}
                                        />
                                        {SOURCE_LABELS[src]} override
                                    </label>
                                    {ov.enabled && (
                                        <Box marginTop='0.5rem'>
                                            <Box display='flex' style={{ gap: '8px' }}>
                                                <Box flex='1'>
                                                    <FormLabel style={{ fontSize: '12px' }}>Lead Price (€)</FormLabel>
                                                    <OutlinedInput
                                                        fullWidth
                                                        type='number'
                                                        value={ov.leadPrice}
                                                        placeholder='Base price'
                                                        onChange={(e) => setOverrideField(src, 'leadPrice', e.target.value)}
                                                    />
                                                </Box>
                                                <Box flex='1'>
                                                    <FormLabel style={{ fontSize: '12px' }}>Weekly Cap</FormLabel>
                                                    <OutlinedInput
                                                        fullWidth
                                                        type='number'
                                                        value={ov.weeklyCap}
                                                        placeholder='Unlimited'
                                                        onChange={(e) => setOverrideField(src, 'weeklyCap', e.target.value)}
                                                    />
                                                </Box>
                                            </Box>
                                            <Box marginTop='0.5rem'>
                                                <FormLabel style={{ fontSize: '12px' }}>Counties</FormLabel>
                                                <Select
                                                    multiple
                                                    fullWidth
                                                    size='small'
                                                    value={ov.counties}
                                                    onChange={(e) => setOverrideField(src, 'counties', e.target.value)}
                                                    renderValue={(selected) => selected.length ? selected.join(', ') : 'Select counties'}
                                                    displayEmpty
                                                >
                                                    {ALL_IRISH_COUNTIES.map((c) => (
                                                        <MenuItem key={c} value={c}>{c}</MenuItem>
                                                    ))}
                                                </Select>
                                                <Box fontSize='11px' color='#8898aa' marginTop='0.25rem'>
                                                    Only leads from {SOURCE_LABELS[src]} in these counties go to this delivery. Leave empty to use the base coverage for this source.
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )
                        })}
                    </FormGroup>
                )}
                <FormGroup>
                    <FormLabel>Billing Mode</FormLabel>
                    <Select
                        fullWidth
                        value={billingMode}
                        onChange={(e) => setBillingMode(e.target.value)}
                        size='small'
                    >
                        <MenuItem value='actual'>Actual (bill per lead delivered)</MenuItem>
                        <MenuItem value='cap'>Cap (bill up to weekly cap regardless)</MenuItem>
                    </Select>
                    <Box fontSize='11px' color='#8898aa' marginTop='0.25rem'>
                        Actual = pay per lead received. Cap = guaranteed payment for full cap allocation each week.
                    </Box>
                </FormGroup>
                <FormGroup>
                    <FormLabel>Billing Company</FormLabel>
                    <Select
                        fullWidth
                        value={billingCompanyId}
                        onChange={(e) => setBillingCompanyId(e.target.value)}
                        size='small'
                        displayEmpty
                    >
                        <MenuItem value=''>Same as delivery company (default)</MenuItem>
                        {companies
                            .filter((c) => c.companyId !== delivery.companyId)
                            .map((company) => (
                                <MenuItem key={company.companyId} value={company.companyId}>{company.name}</MenuItem>
                            ))}
                    </Select>
                    <Box fontSize='11px' color='#8898aa' marginTop='0.25rem'>
                        Bill this delivery's leads to a different company (parent/payer). The invoice contact, billing terms and invoice email come from the selected company; the leads and profile stay with {delivery.companyName || 'the delivery company'}.
                    </Box>
                </FormGroup>
                <FormGroup>
                    <FormLabel>Min EE Leads</FormLabel>
                    <OutlinedInput
                        fullWidth
                        type='number'
                        value={minEELeads}
                        placeholder='Leave blank for none'
                        onChange={(e) => setMinEELeads(e.target.value)}
                    />
                    <Box fontSize='11px' color='#8898aa' marginTop='0.25rem'>
                        Minimum EE leads to prioritise before normal allocation. Leave blank for none.
                    </Box>
                </FormGroup>
                {minEELeads !== '' && parseInt(minEELeads) > 0 && (
                    <FormGroup>
                        <FormLabel>EE Lead Mode</FormLabel>
                        <FormControl variant='outlined' fullWidth>
                            <Select
                                multiple={false}
                                value={eeLeadMode || 'mixed'}
                                onChange={(e) => setEeLeadMode(e.target.value)}
                                input={<OutlinedInput />}
                                style={{marginBottom: '0.5rem'}}
                            >
                                <MenuItem value='first'>EE First — block SF until min EE met</MenuItem>
                                <MenuItem value='mixed'>Mixed — EE sprinkled with SF</MenuItem>
                            </Select>
                        </FormControl>
                        <Box fontSize='11px' color='#8898aa' marginTop='0.25rem'>
                            'EE First' gets 3× priority for EE leads and blocks SF until target met. 'Mixed' receives both throughout.
                        </Box>
                    </FormGroup>
                )}
                <FormGroup>
                    <FormLabel>Active</FormLabel>
                    <FormControl variant='outlined' fullWidth>
                        <Select
                            multiple={false}
                            value={active}
                            onChange={(e) => setActive(e.target.value)}
                            input={<OutlinedInput />}
                            style={{marginBottom: '0.5rem'}}
                        >
                            <MenuItem value={true}>Yes</MenuItem>
                            <MenuItem value={false}>No</MenuItem>
                        </Select>
                    </FormControl>
                </FormGroup>
                <FormGroup>
                    <FormLabel>Priority</FormLabel>
                    <FormControl variant='outlined' fullWidth>
                        <Select
                            multiple={false}
                            value={priority}
                            onChange={(e) => setPriority(e.target.value)}
                            input={<OutlinedInput />}
                            style={{marginBottom: '0.5rem'}}
                        >
                            <MenuItem value={true}>Yes</MenuItem>
                            <MenuItem value={false}>No</MenuItem>
                        </Select>
                    </FormControl>
                    <Box fontSize='11px' color='#8898aa' marginTop='0.25rem'>
                        When enabled, only this provider (and other active priority providers) will receive leads in their coverage area. Non-priority providers are excluded from the pool entirely.
                    </Box>
                </FormGroup>

                <FormGroup>
                    <FormLabel>Weekend Leads</FormLabel>
                    <FormControl variant='outlined' fullWidth>
                        <Select
                            multiple={false}
                            value={weekendLeads}
                            onChange={(e) => setWeekendLeads(e.target.value)}
                            input={<OutlinedInput />}
                            style={{marginBottom: '0.5rem'}}
                        >
                            <MenuItem value={true}>Yes</MenuItem>
                            <MenuItem value={false}>No</MenuItem>
                        </Select>
                    </FormControl>
                    <Box fontSize='11px' color='#8898aa' marginTop='0.25rem'>
                        When disabled, this delivery will not receive leads on Saturdays and Sundays.
                    </Box>
                </FormGroup>
                <FormGroup>
                    <FormLabel>Business Hours Only</FormLabel>
                    <FormControl variant='outlined' fullWidth>
                        <Select
                            multiple={false}
                            value={businessHoursOnly}
                            onChange={(e) => setBusinessHoursOnly(e.target.value)}
                            input={<OutlinedInput />}
                            style={{marginBottom: '0.5rem'}}
                            disabled={weekendLeads !== false}
                        >
                            <MenuItem value={true}>Yes</MenuItem>
                            <MenuItem value={false}>No</MenuItem>
                        </Select>
                    </FormControl>
                    <Box fontSize='11px' color='#8898aa' marginTop='0.25rem'>
                        When enabled, this delivery only receives leads Mon–Fri between 08:00–18:00 Ireland time. Requires Weekend Leads to be set to No.
                    </Box>
                </FormGroup>

                <FormGroup>
                    <FormLabel>Reject Duplicates</FormLabel>
                    <FormControl variant='outlined' fullWidth>
                        <Select
                            multiple={false}
                            value={rejectDuplicates}
                            onChange={(e) => setRejectDuplicates(e.target.value)}
                            input={<OutlinedInput />}
                            style={{marginBottom: '0.5rem'}}
                        >
                            <MenuItem value={true}>Yes</MenuItem>
                            <MenuItem value={false}>No</MenuItem>
                        </Select>
                    </FormControl>
                    <Box fontSize='11px' color='#8898aa' marginTop='0.25rem'>
                        When enabled, a repeat lead from the same person within the duplicate window is rejected instead of being re-sent to this delivery. Default (No) re-sends duplicates as free leads.
                    </Box>
                </FormGroup>

                <FormGroup>
                    <FormLabel>Surge</FormLabel>
                    <FormControl variant='outlined' fullWidth>
                        <Select
                            multiple={false}
                            value={surgeEnabled}
                            onChange={(e) => {
                                setSurgeEnabled(e.target.value)
                                if (!e.target.value) setSurgePercent('')
                            }}
                            input={<OutlinedInput />}
                            style={{marginBottom: '0.5rem'}}
                        >
                            <MenuItem value={true}>Enabled</MenuItem>
                            <MenuItem value={false}>Disabled</MenuItem>
                        </Select>
                    </FormControl>
                    <Box fontSize='11px' color='#8898aa' marginTop='0.25rem'>
                        When enabled, allows flexible over/under delivery by a percentage of the weekly cap. The difference carries over to adjust next week's cap.
                    </Box>
                </FormGroup>
                {surgeEnabled && (
                    <FormGroup>
                        <FormLabel>Surge % (of weekly cap)</FormLabel>
                        <OutlinedInput
                            fullWidth
                            type='number'
                            value={surgePercent}
                            placeholder='e.g. 10 for ±10%'
                            onChange={(e) => setSurgePercent(e.target.value)}
                        />
                        <Box fontSize='11px' color='#8898aa' marginTop='0.25rem'>
                            Percentage of weekly cap allowed as surge. E.g. 10% on a cap of 160 = ±16 leads flexibility.
                        </Box>
                    </FormGroup>
                )}
                {surgeEnabled && delivery.surgeAdjustment != null && delivery.surgeAdjustment !== 0 && (
                    <FormGroup>
                        <FormLabel>Current Surge Adjustment</FormLabel>
                        <Box 
                            padding='0.75rem 1rem' 
                            borderRadius='4px' 
                            border='1px solid #e9ecef'
                            fontWeight='600'
                            color={delivery.surgeAdjustment > 0 ? '#f5365c' : '#2dce89'}
                        >
                            {delivery.surgeAdjustment > 0 ? '+' : ''}{delivery.surgeAdjustment} leads
                            <Box fontSize='11px' color='#8898aa' fontWeight='400' marginTop='0.25rem'>
                                Effective cap this week: {delivery.weeklyCap != null ? delivery.weeklyCap - (delivery.surgeAdjustment || 0) : '∞'}
                            </Box>
                            <Box fontSize='11px' color='#8898aa' fontWeight='400' marginTop='0.25rem'>
                                {delivery.surgeAdjustment > 0 
                                    ? 'Over-delivered in previous weeks — cap is reduced this week to compensate.' 
                                    : 'Under-delivered in previous weeks — cap is increased this week to compensate.'}
                                {' '}Clamped to ±{delivery.weeklyCap != null && delivery.surgePercent != null ? Math.floor(delivery.weeklyCap * delivery.surgePercent / 100) : '?'} leads ({delivery.surgePercent || 0}% of cap).
                            </Box>
                        </Box>
                    </FormGroup>
                )}
                {surgeEnabled && (
                    <FormGroup>
                        <FormLabel>Surge Balance</FormLabel>
                        <Box 
                            padding='0.75rem 1rem' 
                            borderRadius='4px' 
                            border='1px solid #e9ecef'
                            fontWeight='600'
                            color={(delivery.surgeBalance || 0) > 0 ? '#f5365c' : (delivery.surgeBalance || 0) < 0 ? '#2dce89' : '#8898aa'}
                        >
                            {(delivery.surgeBalance || 0) > 0 ? '+' : ''}{delivery.surgeBalance || 0} leads
                            {delivery.leadPrice && (delivery.surgeBalance || 0) !== 0 && (
                                <span style={{ fontSize: '12px', color: '#8898aa', fontWeight: 400, marginLeft: '8px' }}>
                                    (€{Math.abs((delivery.surgeBalance || 0) * delivery.leadPrice).toFixed(2)}{' '}
                                    {(delivery.surgeBalance || 0) > 0 ? 'owed' : 'credit'})
                                </span>
                            )}
                            <Box fontSize='11px' color='#8898aa' fontWeight='400' marginTop='0.25rem'>
                                Cumulative over/under delivery vs invoiced cap. Positive = over-delivered (provider owes). Negative = under-delivered (credit owed).
                            </Box>
                        </Box>
                    </FormGroup>
                )}
                {!surgeEnabled && (delivery.surgeBalance || 0) !== 0 && (
                    <FormGroup>
                        <FormLabel>Outstanding Surge Balance</FormLabel>
                        <Box 
                            padding='0.75rem 1rem' 
                            borderRadius='4px' 
                            border='1px solid #ffd600'
                            backgroundColor='#fff8e1'
                            fontWeight='600'
                            color={(delivery.surgeBalance || 0) > 0 ? '#f5365c' : '#2dce89'}
                        >
                            {(delivery.surgeBalance || 0) > 0 ? '+' : ''}{delivery.surgeBalance || 0} leads
                            {delivery.leadPrice && (
                                <span style={{ fontSize: '12px', color: '#525f7f', fontWeight: 400, marginLeft: '8px' }}>
                                    (€{Math.abs((delivery.surgeBalance || 0) * delivery.leadPrice).toFixed(2)}{' '}
                                    {(delivery.surgeBalance || 0) > 0 ? 'owed' : 'credit'})
                                </span>
                            )}
                            <Box fontSize='11px' color='#525f7f' fontWeight='400' marginTop='0.25rem'>
                                Surge is disabled but there is an unsettled balance. Settle this manually before archiving.
                            </Box>
                        </Box>
                    </FormGroup>
                )}

                {/* Read-only info */}
                <FormGroup>
                    <FormLabel>HK Commission</FormLabel>
                    <FormControl variant='outlined' fullWidth>
                        <Select
                            multiple={false}
                            value={hkCommission}
                            onChange={(e) => setHkCommission(e.target.value)}
                            input={<OutlinedInput />}
                            style={{marginBottom: '0.5rem'}}
                        >
                            <MenuItem value={true}>Yes</MenuItem>
                            <MenuItem value={false}>No</MenuItem>
                        </Select>
                    </FormControl>
                    <Box fontSize='11px' color='#8898aa' marginTop='0.25rem'>
                        When enabled, HK commission is calculated for this delivery.
                    </Box>
                </FormGroup>
                {hkCommission && (
                    <FormGroup>
                        <FormLabel>Pre-HK Leads (per week)</FormLabel>
                        <OutlinedInput
                            fullWidth
                            type='number'
                            value={preHkLeads}
                            placeholder='e.g. 10'
                            onChange={(e) => setPreHkLeads(e.target.value)}
                        />
                        <Box fontSize='11px' color='#8898aa' marginTop='0.25rem'>
                            Leads this provider was already taking per week before HK got involved. HK commission is calculated on leads above this number.
                        </Box>
                    </FormGroup>
                )}

                {/* Billing type */}
                <FormGroup>
                    <FormLabel>Billing Type</FormLabel>
                    <Box 
                        padding='0.75rem 1rem' 
                        borderRadius='4px' 
                        border='1px solid #e9ecef'
                        fontWeight='600'
                        color='#525f7f'
                    >
                        {delivery.balance != null ? 'Prepaid (Balance)' : 'Invoice'}
                        {delivery.balance != null && (
                            <Box fontSize='12px' color={delivery.balance > 0 ? '#2dce89' : '#f5365c'} marginTop='0.25rem'>
                                Balance: €{delivery.balance.toFixed(2)}
                            </Box>
                        )}
                    </Box>
                </FormGroup>
			</DialogContent>
            <DialogActions>
                <Box width='100%' display='flex' justifyContent='space-between'>
                    <Button onClick={() => props.close()} color='primary'>
                        Close
                    </Button>
                    <Box>
                        <Box display='inline-block' marginRight='0.5rem'>
                            <Button
                                color='primary'
                                variant='contained'
                                onClick={() => handleSave()}
                            >
                                Save
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </DialogActions>
            <Dialog
                open={capChangeDialog}
                onClose={() => setCapChangeDialog(false)}
                maxWidth='xs'
                fullWidth
            >
                <div className={classes.dialogHeader}>
                    <Typography variant='h5' component='h5' className={classes.dialogTitle}>
                        Apply cap change
                    </Typography>
                    <IconButton onClick={() => setCapChangeDialog(false)}>
                        <Clear />
                    </IconButton>
                </div>
                <DialogContent>
                    <Box fontSize='13px' color='#525f7f' marginBottom='1rem'>
                        You're changing {delivery.companyName || 'this delivery'}'s weekly cap from{' '}
                        <strong>{delivery.weeklyCap != null ? delivery.weeklyCap : '∞'}</strong> to{' '}
                        <strong>{weeklyCap === '' ? '∞' : weeklyCap}</strong>. When should it take effect?
                    </Box>
                    <Box
                        padding='0.5rem 0.75rem'
                        borderRadius='4px'
                        border='1px solid #fb6340'
                        backgroundColor='#fff5f2'
                        fontSize='12px'
                        color='#c53727'
                    >
                        ⚠ <strong>Now</strong> applies immediately and may impact this week's invoice — the invoice cron runs Saturday ~03:00 (Dublin). <strong>Next week</strong> keeps the current cap live and applies the new one automatically after the cron.
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Box width='100%' display='flex' justifyContent='space-between'>
                        <Button onClick={() => setCapChangeDialog(false)} color='primary'>
                            Back
                        </Button>
                        <Box>
                            <Box display='inline-block' marginRight='0.5rem'>
                                <Button onClick={() => updateDelivery('now')} color='primary'>
                                    Apply now
                                </Button>
                            </Box>
                            <Button onClick={() => updateDelivery('next')} color='primary' variant='contained'>
                                Schedule next week
                            </Button>
                        </Box>
                    </Box>
                </DialogActions>
            </Dialog>
            <TestPayloadDialog
                open={testDialog.open}
                title={testDialog.kind === 'webhook' ? 'Test Webhook Delivery' : testDialog.kind === 'email' ? 'Test Email Delivery' : 'Test SolarStack Delivery'}
                description='Review and edit the test payload before sending. This is the exact data that will be sent.'
                product={testDialog.product}
                productOptions={eeProductOptions}
                onProductChange={changeTestProduct}
                defaultPayload={testDialog.payload}
                onSend={sendTest}
                onClose={closeTestDialog}
                sendLabel='Send Test'
            />
        </Dialog>
	)
}
